import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Download, Send, Mail, Loader2, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PaginationBar } from './PaginationBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

export function AdminSubmissionsTab({
  filteredSubmissions, submissions, dateFrom, setDateFrom, dateTo, setDateTo,
  downloadCSV, paidEmails, setSelectedSubmission, generatingForId,
  handleAdminGenerateReport, handleDownloadPaidReport, formatDate, onRefresh,
}) {
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const searchedSubmissions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return filteredSubmissions;
    return filteredSubmissions.filter(s => {
      const u = s.user_data || {};
      return (
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.company_name || '').toLowerCase().includes(q)
      );
    });
  }, [filteredSubmissions, search]);

  const totalPages = Math.max(1, Math.ceil(searchedSubmissions.length / PAGE_SIZE));
  const pagedSubmissions = useMemo(
    () => searchedSubmissions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [searchedSubmissions, page]
  );

  // Reset to page 1 when filters change the list size or current page exceeds range
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);
  useEffect(() => { setPage(1); }, [dateFrom, dateTo, search]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = (checked) => {
    if (checked) setSelected(new Set(pagedSubmissions.map(s => s.id)));
    else setSelected(new Set());
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    setDeleting(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/submissions/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Submission deleted'); onRefresh?.(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed to delete'); }
    } catch { toast.error('Network error'); }
    setDeleting(null);
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} submissions?`)) return;
    setDeleting('bulk');
    try {
      const res = await fetch(`${API_URL}/api/admin/submissions/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Deleted ${d.deleted} submissions`);
        setSelected(new Set());
        onRefresh?.();
      } else { const e = await res.json(); toast.error(e.detail || 'Failed to delete'); }
    } catch { toast.error('Network error'); }
    setDeleting(null);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle>All Submissions</CardTitle>
          <div className="flex flex-wrap items-end gap-3">
            <div className="relative">
              <Label className="text-xs text-gray-500 mb-1 block">Search</Label>
              <Search className="absolute left-2.5 top-[30px] h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Name, email, or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-[240px] text-sm pl-8"
                data-testid="submissions-search-input"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[150px] text-sm" data-testid="date-from-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[150px] text-sm" data-testid="date-to-input" />
            </div>
            {(dateFrom || dateTo || search) && (
              <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setSearch(''); }} className="text-gray-500 hover:text-gray-700 h-9" data-testid="clear-date-filter-btn">Clear</Button>
            )}
            <Button variant="outline" size="sm" onClick={downloadCSV} disabled={filteredSubmissions.length === 0} className="h-9" data-testid="download-csv-btn">
              <Download className="h-4 w-4 mr-1.5" />
              Download CSV ({filteredSubmissions.length})
            </Button>
          </div>
        </div>
        {(dateFrom || dateTo || search) && (
          <p className="text-sm text-gray-500 mt-2">Showing {searchedSubmissions.length} of {submissions.length} submissions</p>
        )}
      </CardHeader>
      <CardContent>
        {/* Bulk delete bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-3 mb-4" data-testid="bulk-delete-bar">
            <span className="text-sm font-medium text-red-800">{selected.size} selected</span>
            <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={deleting === 'bulk'} data-testid="bulk-delete-btn">
              {deleting === 'bulk' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete Selected
            </Button>
            <button onClick={() => setSelected(new Set())} className="text-sm text-gray-500 hover:text-gray-700 underline ml-auto">Clear</button>
          </div>
        )}

        {searchedSubmissions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Mail className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>{submissions.length === 0 ? 'No submissions yet' : 'No submissions match your filters'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input type="checkbox" onChange={e => selectAll(e.target.checked)}
                      checked={pagedSubmissions.length > 0 && pagedSubmissions.every(s => selected.has(s.id))}
                      data-testid="select-all-submissions" />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dominant Archetype</TableHead>
                  <TableHead>Secondary Archetype</TableHead>
                  <TableHead>Submitted At</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedSubmissions.map((submission) => {
                  const isPaid = paidEmails.has((submission.user_data?.email || '').toLowerCase());
                  return (
                    <TableRow key={submission.id} className={selected.has(submission.id) ? 'bg-red-50/50' : ''}>
                      <TableCell>
                        <input type="checkbox" checked={selected.has(submission.id)}
                          onChange={() => toggleSelect(submission.id)} />
                      </TableCell>
                      <TableCell className="font-medium">{submission.user_data?.name}</TableCell>
                      <TableCell>{submission.user_data?.company_name}</TableCell>
                      <TableCell>{submission.user_data?.email}</TableCell>
                      <TableCell>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${isPaid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`status-${submission.id}`}>
                          {isPaid ? 'Paid' : 'Unpaid'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">{submission.results?.dominant}</span>
                      </TableCell>
                      <TableCell>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">{submission.results?.secondary}</span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">{formatDate(submission.submitted_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setSelectedSubmission(submission)}>View Details</Button>
                          {!isPaid && (
                            <Button size="sm" disabled={generatingForId === submission.id || !submission.user_data?.email || submission.user_data?.email === 'None'} onClick={() => handleAdminGenerateReport(submission)} style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }} className="text-white hover:opacity-90 text-xs disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`generate-report-${submission.id}`}>
                              {generatingForId === submission.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                              Generate & Send
                            </Button>
                          )}
                          {isPaid && (
                            <Button size="sm" variant="outline" onClick={() => handleDownloadPaidReport(submission.user_data?.email)} className="text-xs" data-testid={`download-report-${submission.id}`}>
                              <Download className="h-3 w-3 mr-1" />
                              Download Report
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(submission.id)} disabled={deleting === submission.id}
                            data-testid={`delete-submission-${submission.id}`}>
                            {deleting === submission.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <PaginationBar page={page} totalPages={totalPages} total={searchedSubmissions.length}
              pageSize={PAGE_SIZE} onChange={setPage} testIdPrefix="submissions-pagination" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
